import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import { Active } from "@/components/features/WorkOrders/Active";
import { CompletedArchived } from "@/components/features/WorkOrders/CompletedOrArchived";
import { NeedsReview } from "@/components/features/WorkOrders/NeedsReview";
import { NewWorkOrderTable } from "@/components/features/WorkOrders/SPWorkOrder/SPWorkOrderTables/NewWorkOrderTable";
import { UpcomingWorkOrder } from "@/components/features/WorkOrders/UpcomingWorkOrder";
import { Button } from "@/components/ui/Button/Button";
import { usePermissions } from "@/hooks/auth/usePermissions";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { useRequestScope } from "@/hooks/useRequestScope";
import { usePersistedTabs } from "@/hooks/usePersistedTabs";
import { tableSearchSchema } from "@/hooks/useTableUrlPersistence";
import { useTableFilterBaseStore } from "@/store/tableFilterStore";
import { useActiveSession } from "@/hooks/useActiveSession";
import type { TabItemType } from "@/types/common.types";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { getRouteIcon } from "@/utils/getRouteIconComponent";
import { Flex, Group, Tabs, Title } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useRef } from "react";
import { z } from "zod";
import styles from "./work-orders.module.scss";
import { AutoDispatch } from "@/components/features/WorkOrders/AutoDispatch";
import { ActiveWorkOrderTable } from "@/components/features/WorkOrders/SPWorkOrder/SPWorkOrderTables/ActiveWorkOrderTable";

// SERV-2297: Auto Dispatch tab hidden for MVP, pending product confirm. Flip to true to restore.
const SHOW_AUTO_DISPATCH_TAB = false;

// Extend the base table search schema to preserve the `tab` param that
// usePersistedTabs writes to the URL alongside table filter params.
const workOrdersSearchSchema = tableSearchSchema.extend({
  tab: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_authenticated/work-orders/")({
  component: WorkOrders,
  validateSearch: workOrdersSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function WorkOrders() {
  const navigate = useNavigate();
  const searchParams = useSearch({ from: "/_authenticated/work-orders/" });
  const requestScope = useRequestScope();

  useBreadcrumb(
    [
      {
        title: "Work Orders",
        href: "/work-orders",
        icon: getRouteIcon(MODULE_KEYS_MAP.workOrders),
      },
    ],
    { listView: "List View" },
  );
  const { hasAccess, isServiceProvider, isReactiveHire } = usePermissions();
  const { create: canCreateWorkOrders } = hasAccess({
    module: MODULE_KEYS_MAP.workOrders,
  });

  const { userType } = useActiveSession();

  const allFilters = useTableFilterBaseStore((s) => s.filters);
  const allFiltersRef = useRef(allFilters);
  allFiltersRef.current = allFilters;

  const { activeTab, setActiveTab } = usePersistedTabs({
    defaultTab: userType
      ? isServiceProvider
        ? isReactiveHire
          ? "activeWorkOrders"
          : "NewWorkOrders"
        : "active"
      : "",
  });

  // Define tab configuration once
  const tabsData: TabItemType[] = !isServiceProvider
    ? [
        {
          value: "active",
          label: "Active",
          component: <Active isActive={activeTab === "active"} searchParams={searchParams} />,
        },
        {
          value: "upcoming",
          label: "Upcoming Visits (Today & Tomorrow)",
          component: (
            <UpcomingWorkOrder isActive={activeTab === "upcoming"} searchParams={searchParams} />
          ),
        },
        {
          value: "completed",
          label: "Completed / Archived",
          component: (
            <CompletedArchived isActive={activeTab === "completed"} searchParams={searchParams} />
          ),
        },
        // SERV-2682: work orders the creation bot extracted but could not
        // finish. Internal users only; the SP branch below does not get it.
        {
          value: "needsReview",
          label: "Needs Review",
          component: (
            <NeedsReview isActive={activeTab === "needsReview"} searchParams={searchParams} />
          ),
        },
        ...(SHOW_AUTO_DISPATCH_TAB
          ? [
              {
                value: "autoDispatch",
                label: "Auto Dispatch",
                component: (
                  <AutoDispatch
                    isActive={activeTab === "autoDispatch"}
                    searchParams={searchParams}
                  />
                ),
              },
            ]
          : []),
      ]
    : [
        ...(!isReactiveHire
          ? [
              {
                value: "NewWorkOrders",
                label: "New",
                component: (
                  <NewWorkOrderTable
                    isActive={activeTab === "NewWorkOrders"}
                    searchParams={searchParams}
                  />
                ),
              },
            ]
          : []),
        {
          value: "activeWorkOrders",
          label: "Active",
          component: (
            <ActiveWorkOrderTable
              isActive={activeTab === "activeWorkOrders"}
              searchParams={searchParams}
            />
          ),
        },
        {
          value: "CloseWorkOrders",
          label: "Done",
          component: (
            <CompletedArchived
              isActive={activeTab === "CloseWorkOrders"}
              searchParams={searchParams}
            />
          ),
        },
      ];

  return (
    <Flex direction="column" className={styles.container}>
      <Group justify="space-between" align="center" className={styles.header}>
        <Title order={3} className={styles.title}>
          Work Orders
        </Title>
        {canCreateWorkOrders && !isServiceProvider && (
          <Button
            variant="primary"
            style={{ height: "var(--component-size-small)" }}
            iconLeft={<IconPlus size={16} />}
            onClick={() => navigate({ to: "/work-orders/add-work-order" })}
          >
            New Work Order
          </Button>
        )}
      </Group>

      <Tabs
        key={requestScope}
        value={activeTab}
        onChange={(value) => setActiveTab(value || "active")}
        className={styles.tab}
      >
        <Tabs.List className={styles.tabsList}>
          {tabsData?.map((tab: TabItemType) => (
            <Tabs.Tab key={tab?.value} value={tab?.value} className={styles.tab}>
              {tab?.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {tabsData?.map((tab: TabItemType) => (
          <Tabs.Panel key={tab?.value} value={tab?.value} className={styles.tabPanel}>
            {tab?.component}
          </Tabs.Panel>
        ))}
      </Tabs>
    </Flex>
  );
}
